import * as React from 'react';
import { clamp } from '../math';

/**
 * Encapsulates logic to drag an element.
 *
 * @returns
 */
export function useDrag1dHandler(args: {
  /** external value
   *
   * always takes precedence over the drag with
   * and changing the given value chancels the resize
   */
  value: number;
  /** defaults to 42 */
  minValue?: number;
  maxValue?: number;
  /** Is called with the new value after the element was dragged. Should be STABLE */
  handleDragEnded?: (value: number) => void;
  direction: 'horizontal' | 'vertical';
  disabled?: boolean;
}): {
  /**
   * the current value
   *
   * is the given (external) value when not dragging and the temporary size while dragging
   */
  value: number;
  isDragging: boolean;
  /** the event handlers to use on a drag-handler-element */
  dragHandlers: NonNullable<
    Pick<
      React.DOMAttributes<HTMLElement>,
      'onMouseDown' | 'onMouseUp' | 'onTouchStart' | 'onTouchCancel' | 'onTouchEnd'
    >
  >;
} {
  const {
    handleDragEnded,
    value: externalValue,
    minValue: minValue = 42,
    maxValue: maxValue = Number.MAX_SAFE_INTEGER,
    direction,
    disabled,
  } = args;

  const [value, setValue] = React.useState(externalValue);
  React.useEffect(() => {
    // cancel drag + use external value
    handleTouchEnd();
    setValue(externalValue);
  }, [externalValue]);

  const [draggingInfo, setDraggingInfo] = React.useState<null | {
    startX: number;
    startY: number;
    startValue: number;
  }>(null);
  const isDragging = draggingInfo !== null;

  function handleTouchStart(event: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) {
    if (isDragging) {
      console.warn(`Duplicate drag-start => abort.`);
      handleTouchEnd();
      return;
    }

    // prevent selection and bubbeling
    event.preventDefault();
    event.stopPropagation();

    let startX = 0;
    let startY = 0;
    const { nativeEvent } = event;
    if (nativeEvent instanceof MouseEvent) {
      startX = nativeEvent.clientX;
      startY = nativeEvent.clientY;

      // When user click with right button the resize is stuck in resizing mode
      // until users clicks again, don't continue if right click is used.
      if (nativeEvent.button === 3) {
        return;
      }
    } else if (nativeEvent instanceof TouchEvent) {
      startX = nativeEvent.touches[0].clientX;
      startY = nativeEvent.touches[0].clientY;
    }

    setDraggingInfo({
      startX,
      startY,
      startValue: externalValue,
    });
  }

  const handleTouchEnd = React.useCallback(
    (event?: MouseEvent | TouchEvent | React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
      if (!isDragging) {
        return; // noop
      }
      setDraggingInfo(null);

      event?.preventDefault();

      if (handleDragEnded && value !== externalValue) {
        handleDragEnded(value);
      }
    },
    [isDragging, setDraggingInfo, handleDragEnded, value, externalValue],
  );

  // only track mouse position while dragging
  React.useEffect(() => {
    const handleMouseMove = (event: MouseEvent | TouchEvent) => {
      if (draggingInfo === null) {
        return;
      }
      const { startValue, startX, startY } = draggingInfo;

      const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
      const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;

      const delta = direction === 'horizontal' ? clientX - startX : clientY - startY;
      const deltaValue = startValue + delta;
      const newValue = clamp(deltaValue, minValue, maxValue);

      setValue(newValue);
    };

    if (isDragging === true) {
      // register on leading edge
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('touchmove', handleMouseMove);

      document.addEventListener('mouseup', handleTouchEnd);
      document.addEventListener('mouseleave', handleTouchEnd);
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('touchmove', handleMouseMove);

        document.removeEventListener('mouseup', handleTouchEnd);
        document.removeEventListener('mouseleave', handleTouchEnd);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
    return undefined;
  }, [draggingInfo, isDragging, direction, minValue, maxValue, handleTouchEnd]);

  const resizeHandlers: NonNullable<
    Pick<
      React.DOMAttributes<HTMLElement>,
      'onMouseDown' | 'onMouseUp' | 'onTouchStart' | 'onTouchCancel' | 'onTouchEnd'
    >
  > = {
    onMouseDown: handleTouchStart,
    onTouchStart: handleTouchStart,

    onMouseUp: handleTouchEnd,
    onTouchCancel: handleTouchEnd,
    onTouchEnd: handleTouchEnd,
  };

  if (disabled) {
    return {
      value: externalValue,
      isDragging: false,
      dragHandlers: {},
    };
  }

  return {
    value,
    isDragging,
    dragHandlers: resizeHandlers,
  };
}
